// Fire date of one reminder from src/lib/local-notifications.js. Kept free of
// Capacitor and i18n imports so the unit tests can exercise it under node.

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * When a reminder should next fire, on the device's own clock.
 *
 * Two shapes:
 *  • `days: N`        — N days from now.
 *  • `endOfMonth: true` — the real last day of the month, 28/29/30/31 as the
 *    calendar has it.
 *
 * Both are single dates, re-armed on every app open. The OS repeat rules are
 * deliberately unused: `on: { day: 31 }` skips every shorter month, and the
 * `month` field means January=1 on iOS but January=0 on Android.
 *
 * @param {{days?: number, endOfMonth?: boolean, hour?: number, minute?: number}} reminder
 * @param {Date} now
 * @returns {Date}
 */
export function nextFireDate(reminder, now = new Date()) {
    const hour = reminder.hour ?? 19
    const minute = reminder.minute ?? 0

    if (reminder.endOfMonth) {
        // Day 0 of month N+1 is the last day of month N, leap years included.
        const lastDay = (monthsAhead) =>
            new Date(now.getFullYear(), now.getMonth() + monthsAhead + 1, 0, hour, minute, 0, 0)

        const thisMonth = lastDay(0)
        // Already gone by → next month's, since the OS drops a past date.
        return thisMonth > now ? thisMonth : lastDay(1)
    }

    const at = new Date(now.getTime() + (reminder.days ?? 0) * DAY_MS)
    at.setHours(hour, minute, 0, 0)
    return at
}
